import { cva } from 'class-variance-authority';
import {
  Body,
  Heading,
  Icon,
  IconButton,
  IconType,
  TextLink,
  TextLinkProps,
} from 'copilot-design-system';
import React from 'react';

export type ToastVariant =
  | 'success'
  | 'error'
  | 'warning'
  | 'neutral'
  | 'info'
  | 'progress';

type Props = {
  variant: ToastVariant;
  title?: string;
  description?: string;
  onClose?: () => void;
  actionProps?: TextLinkProps;
  progressValue?: number; // Value between 0 and 100 for progress variant
};

const iconStyles = cva('h-[13px] w-[13px]', {
  variants: {
    variant: {
      success: 'text-success-primary',
      error: 'text-error-primary',
      info: 'text-info-primary',
      progress: 'text-black-heading',
      warning: 'text-warning-primary',
    },
  },
});

const toastStyles = cva(
  'bg-white rounded-l-sm border-0 border-l-2 border-solid shadow p-4 w-[404px]',
  {
    variants: {
      variant: {
        success: 'border-success-primary',
        error: 'border-error-primary',
        info: 'border-info-primary',
        progress: 'border-black-heading',
        neutral: 'border-black-heading',
        warning: 'border-warning-primary',
      },
    },
  },
);

const RenderIcon = ({ variant }: { variant: ToastVariant }) => {
  const getToastIcon = (): IconType => {
    switch (variant) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Failed';
      case 'info':
        return 'Info';
      case 'progress':
        return 'Upload';
      case 'warning':
        return 'Warning';
      default:
        return 'Success';
    }
  };

  if (variant === 'neutral') return null;

  return <Icon className={iconStyles({ variant })} icon={getToastIcon()} />;
};

export const Toast = ({
  variant,
  title,
  description,
  actionProps,
  onClose,
  progressValue = 0,
}: Props) => {
  const hasTitle = Boolean(title);
  const hasDescription = Boolean(description);

  const isOnlyDescription = !hasTitle && hasDescription;
  const TextComponent = isOnlyDescription ? Body : Heading;
  const primaryText = isOnlyDescription ? description : title;

  return (
    <div className={toastStyles({ variant })}>
      <div className="flex items-center gap-2">
        <RenderIcon variant={variant} />
        <div className="flex-1">
          {primaryText && <TextComponent>{primaryText}</TextComponent>}
        </div>
        <div>{actionProps && <TextLink {...actionProps} />}</div>
        {onClose && (
          <div>
            <IconButton
              icon="Close"
              variant="minimal"
              size="sm"
              onClick={onClose}
            />
          </div>
        )}
      </div>
      {/* Secondary description only if both title and description exist */}
      {hasTitle && hasDescription && (
        <div className="ml-[21px] max-w-[285px]">
          <Body>{description}</Body>
        </div>
      )}
      {/* Progress indicator */}
      {variant === 'progress' && (
        <div className="flex items-center gap-2 ml-[21px]">
          <div className="h-[3px] w-full bg-secondary-hover rounded-full">
            <div
              className="h-full bg-black-heading rounded-full"
              style={{ width: `${progressValue}%` }}
            />
          </div>
          <Heading size="xs">{progressValue}%</Heading>
        </div>
      )}
    </div>
  );
};
